import {get as _get, isEmpty as _isEmpty, isEqual as _isEqual} from 'lodash';

import {refetchQueryByName} from '@utils/apollo';
import {
    formatClipProvidersForSubmit,
    getClipProviderFromPropertyLicence,
    getDefaultClipProvidersValues,
    normalizeClipProvidersValues,
    prefillCoverage,
    resolveClipProvidersDefaults,
    setClipProvidersInForm,
    transformProductsForCoverageDropdown,
    updateClipProvidersFieldBasedOnPreset,
} from '@modules/events/utils/formHelper';
import EventForm from '@modules/events/forms/EventForm';
import {showErrorNoAvailablePresets} from '@modules/events/utils/multipleEventActions';
import {hideModal} from '@utils/modal';

export class EventsMultiplePresetApplyForm extends EventForm {
    prepareDataForSubmit(data) {
        const pl = this.props.GraphQLOptionsData?.propertyLicence;
        const hasSpiideoIntegration = pl?.property_licence_tech_setup?.has_spiideo_integration ?? pl?.has_spiideo_integration;

        return {
            id: 'UPDATE',
            events: this.props.formParams.optionsVariables.eventsIds,
            event_preset: data.event_preset,
            description: data.description || null,
            external_description: data.external_description || null,
            color: data.color.substring(1),
            event_status: data.event_status || null,
            clip_providers: 0 < data.coverage.length ? formatClipProvidersForSubmit(data) : [],
            products: data.coverage,
            notes: data.notes || null,
            as_additional_contents: data.event_preset ? !!data.as_additional_contents : false,
            has_argos_job_scheduling: data.argos_job_scheduling || null,
            as_additional_argos: (data.argos_job_scheduling && data.event_preset) ? data.as_additional_argos : null,
            has_spiideo_integration: hasSpiideoIntegration ? !!data.spiideo_integration : false,
        };
    }

    updateAsAdditionalContentsField = () => {
        const isVisible = !!this.props.formValues.event_preset;

        this.setField('as_additional_contents', {
            hidden: !isVisible,
            ...(!isVisible && {defaultValue: false}),
        });
    };

    componentDidMount() {
        super.componentDidMount();

        this.setUpdateSuccessCallback(() => {
            refetchQueryByName(this.props.formParams.queryForRefetch);
        });

        this.setUpdateErrorCallback((error) => {
            hideModal();
            this.props.MessageBox.addMessage(
                'eventsIndex',
                error.message,
                null,
                'error',
                true
            );
        });

        const products = _get(this.props, 'formParams.optionsVariables.products', []);

        this.setField('coverage', {
            options: transformProductsForCoverageDropdown(products),
            hidden: !products.length,
        });
        this.setField('clip_providers', {
            eventContentTypes: _get(this.props, 'formParams.optionsVariables.eventContentTypes', []),
        });
        this.setState({
            coverageProducts: products,
            eventContentTypes: _get(this.props, 'formParams.optionsVariables.eventContentTypes', []),
            rightsScopes: _get(this.props, 'formParams.optionsVariables.rightsScopes', []),
        });
    }

    componentDidUpdate(prevProps, prevState = {}) {
        super.componentDidUpdate(prevProps);

        const eventPresetChanged = this.props.formValues.event_preset !== prevProps.formValues.event_preset;
        const selectedPresetDataChanged = this.state.selectedPresetData !== prevState.selectedPresetData;
        let nextFormValues = this.props.formValues;

        const {
            GraphQLOptionsData: {
                loading: optionsLoading,
                eventPresets,
                propertyLicence,
                clipProviders,
            },
        } = this.props;

        if (!optionsLoading && !this.state.dataReceived) {
            if (0 === eventPresets.length) {
                showErrorNoAvailablePresets();

                return;
            }

            this.setField('property_licence', {
                options: [{
                    id: propertyLicence.id,
                    text: `${propertyLicence.name} - ${propertyLicence.licensor.name}`,
                    key: propertyLicence.id,
                    value: propertyLicence.id,
                }],
            });
            this.setField('clip_providers', {
                options: clipProviders,
            });

            const optionsForDropdown = [],
                presetsListObject = {};

            for (const preset of eventPresets) {
                presetsListObject[preset.id] = preset;

                optionsForDropdown.push({
                    key: preset.id,
                    value: preset.id,
                    text: preset.name,
                });
            }

            this.setField('event_preset', {
                options: optionsForDropdown,
            });

            if (propertyLicence?.property_licence_tech_setup?.has_argos_job_scheduling) {
                this.setField('argos_job_scheduling', {
                    hidden: false,
                });
            }

            if (propertyLicence?.property_licence_tech_setup?.has_spiideo_integration) {
                this.setField('spiideo_integration', {
                    hidden: false,
                });
            }

            if (this.props.GraphQLOptionsData !== prevProps.GraphQLOptionsData) {
                this.setState(() => ({
                    dataReceived: true,
                    presetsListObject,
                }));
            }
        }

        if ((eventPresetChanged || selectedPresetDataChanged) && this.props.formValues.event_preset && this.props.change) {
            const presetProducts = this.state.selectedPresetData?.event?.products;
            const hasPresetProductsLoaded = Array.isArray(presetProducts);
            const coverageOptions = _get(
                this.state,
                'defaultForm_fields.coverage.props.options',
                transformProductsForCoverageDropdown(_get(this.props, 'formParams.optionsVariables.products', []))
            );
            const selectedPresetProductIds = (presetProducts || []).map((product) => Number(product.id));
            const syncedCoverage = coverageOptions
                .filter((option) => selectedPresetProductIds.includes(Number(option.id ?? option.value)))
                .map((option) => Number(option.value))
                .filter((coverageId) => !Number.isNaN(coverageId));

            if (hasPresetProductsLoaded && !_isEqual(this.props.formValues.coverage || [], syncedCoverage)) {
                this.props.change('coverage', syncedCoverage);
                this.setField('coverage', {
                    defaultValue: syncedCoverage,
                    timestamp: Date.now(),
                });
            }

            if (hasPresetProductsLoaded && 0 === syncedCoverage.length && !_isEmpty(this.props.formValues.clip_providers)) {
                this.props.change('clip_providers', {});
            }

            if (hasPresetProductsLoaded) {
                nextFormValues = {
                    ...nextFormValues,
                    coverage: syncedCoverage,
                    clip_providers: 0 === syncedCoverage.length ? {} : nextFormValues.clip_providers,
                };
            }
        }

        const currentProps = nextFormValues === this.props.formValues
            ? this.props
            : {...this.props, formValues: nextFormValues};
        const coverageChanged = !_isEqual(nextFormValues.coverage, prevProps.formValues.coverage);
        const hasCoverage = 0 < (nextFormValues.coverage || []).length;

        if (coverageChanged && hasCoverage && !nextFormValues.event_preset && this.props.change) {
            const defaultClipProvider = getClipProviderFromPropertyLicence(this.state.rightsScopes, this.state.coverageProducts);
            const formClipProviders = normalizeClipProvidersValues(nextFormValues.clip_providers ?? {});
            const fallbackDefaults = getDefaultClipProvidersValues(
                this.state.eventContentTypes,
                defaultClipProvider ? String(defaultClipProvider) : ''
            );
            const defaultDropdownValues = resolveClipProvidersDefaults(formClipProviders, fallbackDefaults);

            this.setField('clip_providers', {
                hidden: false,
                defaultDropdownValues,
            });
            setClipProvidersInForm(this.props.change, this.state.eventContentTypes, defaultDropdownValues);
        }

        prefillCoverage(
            _get(this.state, 'defaultForm_fields.coverage.props.hidden'),
            this.state.selectedPresetData?.event?.products,
            _get(this.state, 'defaultForm_fields.coverage.props.options', []),
            this.setField,
            this.state.commonProducts,
            (newState) => this.setState(newState)
        );

        updateClipProvidersFieldBasedOnPreset(currentProps, prevProps.formValues, this.setField, this.state, this.state.presetsListObject, {
            setFormValue: this.props.change,
        });

        const {event_preset, argos_job_scheduling} = this.props.formValues;

        if (event_preset !== prevProps.formValues?.event_preset
            || argos_job_scheduling !== prevProps.formValues?.argos_job_scheduling
        ) {
            this.updateAsAdditionalContentsField();
            this.updateAsAdditionalArgosField();
        }
    }

    renderSaveButton(props = {}) {
        return super.renderSaveButton({...props, content: 'Apply'});
    }
}

export default EventsMultiplePresetApplyForm;
